// The chatbot TRANSCRIPT (creative-code-studio-chatbot-prd §3.4 / D-CB-07): the
// conversation a kid's bot has had, owned by the PARENT of the sandboxed page
// and stored ONLY on this device. The server never stores a line of it — it
// receives at most the last {@link CHATBOT_HISTORY_WINDOW} messages per reply
// and keeps wallet/audit rows only.
//
// Two stores, one shape:
//   - the studio → IndexedDB (`projectPersistence`, `transcript:<projectId>`),
//     in-memory under a /try demo (the existing demo swap covers it);
//   - the public `/play/:shareId` host → `localStorage` under
//     `airbotix:chat:<shareId>` (try/catch — a blocked store falls back to memory
//     for the visit, never a crash).
//
// The in-frame `ai.history()` is a synchronous mirror the page draws from; the
// parent appends the kid's line before the call and the bot's line after a
// successful reply, then persists. Capped at {@link CHATBOT_TRANSCRIPT_CAP}
// (oldest dropped).

import { loadChatbotTranscript, saveChatbotTranscript } from './projectPersistence';

export interface ChatTranscriptItem {
  role: 'user' | 'bot';
  text: string;
  /** Epoch ms. */
  at: number;
}

/** How many messages a transcript keeps (oldest dropped past this). */
export const CHATBOT_TRANSCRIPT_CAP = 200;
/** How many of the newest messages ride each reply request as history (D-CB-08). */
export const CHATBOT_HISTORY_WINDOW = 12;
/** The localStorage key prefix the public play host uses. */
export const PUBLIC_TRANSCRIPT_KEY_PREFIX = 'airbotix:chat:';

export interface TranscriptStore {
  load(): Promise<ChatTranscriptItem[]>;
  save(items: ChatTranscriptItem[]): Promise<void>;
}

const isItem = (v: unknown): v is ChatTranscriptItem =>
  typeof v === 'object' &&
  v !== null &&
  ((v as { role?: unknown }).role === 'user' || (v as { role?: unknown }).role === 'bot') &&
  typeof (v as { text?: unknown }).text === 'string' &&
  typeof (v as { at?: unknown }).at === 'number';

/** Keep only well-formed items (a hand-edited / stale store never crashes the page). */
export function sanitizeTranscript(raw: unknown): ChatTranscriptItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isItem).slice(-CHATBOT_TRANSCRIPT_CAP);
}

/** The studio store: IndexedDB under the project id (best-effort, like the chat log). */
export function createStudioTranscriptStore(projectId: string): TranscriptStore {
  return {
    load: async () => sanitizeTranscript(await loadChatbotTranscript(projectId)),
    save: (items) => saveChatbotTranscript(projectId, items),
  };
}

/** The public host store: localStorage keyed by share id, memory fallback. */
export function createPublicTranscriptStore(shareId: string): TranscriptStore {
  const key = PUBLIC_TRANSCRIPT_KEY_PREFIX + shareId;
  let memory: ChatTranscriptItem[] = [];
  return {
    load: async () => {
      try {
        const raw = window.localStorage.getItem(key);
        return raw ? sanitizeTranscript(JSON.parse(raw)) : memory;
      } catch {
        return memory;
      }
    },
    save: async (items) => {
      memory = items;
      try {
        window.localStorage.setItem(key, JSON.stringify(items));
      } catch {
        // Storage blocked (private mode / quota) — the visit keeps the memory copy.
      }
    },
  };
}

/** An in-memory store (the /try demo, tests, or a host with no id yet). */
export function createMemoryTranscriptStore(): TranscriptStore {
  let memory: ChatTranscriptItem[] = [];
  return {
    load: async () => memory,
    save: async (items) => {
      memory = items;
    },
  };
}

/**
 * The parent-owned transcript: a synchronous mirror (what `ai.history()`
 * returns) backed by a best-effort persistent store.
 */
export class ChatbotTranscript {
  private items: ChatTranscriptItem[] = [];
  private loaded: Promise<void> | null = null;

  constructor(private readonly store: TranscriptStore) {}

  /** Load once (idempotent); later calls resolve immediately. */
  init(): Promise<void> {
    if (!this.loaded) {
      this.loaded = this.store
        .load()
        .then((items) => {
          this.items = items;
        })
        .catch(() => {
          this.items = [];
        });
    }
    return this.loaded;
  }

  snapshot(): ChatTranscriptItem[] {
    return this.items.slice();
  }

  append(role: ChatTranscriptItem['role'], text: string): ChatTranscriptItem {
    const item: ChatTranscriptItem = { role, text, at: Date.now() };
    this.items = [...this.items, item].slice(-CHATBOT_TRANSCRIPT_CAP);
    void this.store.save(this.items);
    return item;
  }

  /** Drop the newest item when it is the given one (a failed send's user line). */
  remove(item: ChatTranscriptItem): void {
    const idx = this.items.lastIndexOf(item);
    if (idx === -1) return;
    this.items = [...this.items.slice(0, idx), ...this.items.slice(idx + 1)];
    void this.store.save(this.items);
  }

  clear(): void {
    this.items = [];
    void this.store.save(this.items);
  }

  /** The newest {@link CHATBOT_HISTORY_WINDOW} messages, as the server expects them. */
  historyForServer(): Array<{ role: 'user' | 'bot'; text: string }> {
    return this.items.slice(-CHATBOT_HISTORY_WINDOW).map(({ role, text }) => ({ role, text }));
  }
}
